import { useState, useEffect, useCallback } from 'react';
import { picksAPI, adminAPI } from '../services/api';
import { useApiState } from './useApiState';
import { sortMatches } from '../utils/matchHelpers';

/**
 * Custom hook to manage round navigation and data fetching
 * @param {number} selectedUserId - ID of user being viewed (for admin)
 * @param {boolean} isViewingAsAdmin - Whether admin is viewing another user
 * @returns {Object} Round navigation state and handlers
 */
export const useRoundNavigation = (selectedUserId, isViewingAsAdmin) => {
  const [currentRound, setCurrentRound] = useState(null);
  const [roundInfo, setRoundInfo] = useState(null);
  const [maxRound, setMaxRound] = useState(null);
  const [currentSeasonId, setCurrentSeasonId] = useState(null);
  const [matches, setMatches] = useState([]);
  const { loading, executeRequest } = useApiState();
  const [standingsRefreshKey, setStandingsRefreshKey] = useState(0);

  // Fetch current round on mount
  useEffect(() => {
    const fetchCurrentRound = async () => {
      try {
        const response = await picksAPI.getCurrentRound();
        const { roundNumber } = response.data;
        setCurrentRound(roundNumber);
      } catch (err) {
        console.error('Failed to fetch current round:', err);
        // Fallback to round 1 if API fails
        setCurrentRound(1);
      }
    };
    fetchCurrentRound();
  }, []);

  // Load round data
  const loadRoundData = useCallback(async () => {
    if (currentRound === null) return;

    try {
      const matchesData = await executeRequest(async () => {
        // Load matches for the round with picks embedded
        let picksResponse;
        if (isViewingAsAdmin && selectedUserId) {
          // Admin viewing another user's picks
          picksResponse = await adminAPI.getUserRoundPicks(selectedUserId, currentRound);
        } else {
          // Regular user or admin viewing own picks
          picksResponse = await picksAPI.getRoundPicks(currentRound);
        }

        const matchesData = picksResponse.data.matches || [];
        const roundData = picksResponse.data.round || {};

        // Sort matches
        const sortedMatches = sortMatches(matchesData);
        setMatches(sortedMatches);

        // Store round info
        setRoundInfo({
          seasonYear: roundData.season_year,
          startDate: roundData.start_date,
          endDate: roundData.end_date,
          roundNumber: roundData.round_number,
          maxRound: roundData.max_round_number,
        });
        setMaxRound(roundData.max_round_number);
        setCurrentSeasonId(roundData.season_id);

        return matchesData;
      });

      return matchesData;
    } catch (err) {
      console.error('Failed to load round data:', err);
      throw err;
    }
  }, [currentRound, selectedUserId, isViewingAsAdmin, executeRequest]);

  // Load round data when dependencies change
  useEffect(() => {
    if (currentRound !== null) {
      loadRoundData();
    }
  }, [currentRound, loadRoundData]);

  // Navigation handlers
  const goToPreviousRound = () => {
    setCurrentRound((prev) => Math.max(1, prev - 1));
  };

  const goToNextRound = () => {
    setCurrentRound((prev) => prev + 1);
  };

  const refreshStandings = () => {
    setStandingsRefreshKey((prev) => prev + 1);
  };

  return {
    currentRound,
    roundInfo,
    maxRound,
    currentSeasonId,
    matches,
    loading,
    standingsRefreshKey,
    setMatches,
    goToPreviousRound,
    goToNextRound,
    loadRoundData,
    refreshStandings,
  };
};
